import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../context/ThemeContext";

const LongInputModal = ({
  isOpen,
  onClose,
  onSubmit,
  courseDay,
  isSubmitting,
  onModalStateChange,
}) => {
  // Notify parent when modal opens/closes to hide sidebar
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isOpen);
    }
    // Add/remove class to hide sidebar on body
    if (isOpen) {
      document.body.classList.add("modal-open-hide-sidebar");
    } else {
      document.body.classList.remove("modal-open-hide-sidebar");
    }
    return () => {
      document.body.classList.remove("modal-open-hide-sidebar");
    };
  }, [isOpen, onModalStateChange]);
  const [assignment1, setAssignment1] = useState("");
  const [assignment2, setAssignment2] = useState("");
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const textarea1Ref = useRef(null);
  const textarea2Ref = useRef(null);

  const questionsMap = {
    13: {
      question:
        "Send me the two explorative essays you've written — one describing your experience during and after the meal, and the other from your stomach's point of view.",
      hasTwoInputs: true,
      label1: "Essay 1",
      label2: "Essay 2",
    },
    16: {
      question:
        "'Food As A Person' Explorative Essay. Write essays on the topics: (1) If food were a person, who was it in the past? (2) If food were to become someone new—your ideal relationship—who would it be?",
    },
    21: {
      question: "✍️ Write your own 60–90 second future-food experience script",
    },
    25: {
      question:
        "Write a short script that mentally and emotionally prepares you for real-life success with food.",
    },
    31: {
      question:
        "What have been the wins—big or small—that show how your relationship with food has changed in the past 30 days? If you’ve written them down on your phone as suggested in the morning, copy and paste them here.",
    },
    38: {
      question: `👉 Are you noticing any changes in your relationship with food so far? For example, here are a few changes our users commonly find: 
“I didn’t feel like having the dessert which I’ve always wanted.”
“I’m no longer feeling the urge of completing the plate now.” 
“I was at a social event and I wasn’t thinking about food.”
List down the changes you’ve been seeing and feeling. This process helps you rewire your mind to have a different perspective about how food impacts you right now.`,
    },
    39: {
      question:
        "👉 What are the new beliefs I've formed about myself, food, and how I relate to eating since beginning this journey?",
    },
  };

  if (!isOpen) return null;

  const currentQuestionData = questionsMap[courseDay] || {};
  const currentQuestion =
    currentQuestionData.question || "Please write your response below.";
  const isDay13 = courseDay === 13;

  const handleSubmit = () => {
    if (isDay13) {
      if (assignment1.trim() && assignment2.trim()) {
        onSubmit({ essay1: assignment1, essay2: assignment2 });
        setAssignment1("");
        setAssignment2("");
      }
    } else {
      if (assignment1.trim()) {
        onSubmit(assignment1);
        setAssignment1("");
      }
    }
    // Optional: close modal after submit
    // onClose();
  };

  const textareaClasses = `
    w-full 
    min-h-[180px] 
    max-h-[55vh] 
    p-4 
    rounded-lg 
    resize-y
    font-['Poppins'] 
    focus:outline-none 
    focus:ring-2 
    focus:ring-[#069c27] 
    transition-all
    overflow-auto
    ${
      isDark
        ? "bg-[#1B1F27] text-[#E7EAF0] placeholder-[#5A5E69] border border-[#232733]"
        : "bg-[#F4F4F6] text-[#2E2E2E] placeholder-gray-400 border border-[#E6E6EA]"
    }
  `;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] px-4 font-['Poppins']"
      onClick={onClose}
    >
      <div
        className={`
          max-w-2xl w-full 
          max-h-[90vh] 
          rounded-2xl 
          relative 
          overflow-hidden 
          shadow-2xl 
          flex flex-col
          ${isDark ? "bg-[#0F1115]" : "bg-white"}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className={`
            absolute top-4 right-4 
            text-3xl z-20 
            rounded-full w-10 h-10 
            flex items-center justify-center 
            transition-all hover:scale-110
            ${
              isDark
                ? "text-[#E7EAF0] hover:text-white bg-[#171A20] hover:bg-[#1B1F27] border border-[#232733]"
                : "text-[#2E2E2E] hover:text-gray-700 bg-white hover:bg-gray-50 border border-[#E6E6EA]"
            }
          `}
          aria-label="Close"
        >
          ×
        </button>

        {/* Header */}
        <div
          className={`
            rounded-t-2xl px-6 py-5 border-b flex-shrink-0
            ${isDark ? "bg-[#171A20] border-[#232733]" : "bg-[#F4F4F6] border-[#E6E6EA]"}
          `}
        >
          <h2
            className={`
              text-lg font-semibold text-center font-['Poppins']
              ${isDark ? "text-[#E7EAF0]" : "text-[#2E2E2E]"}
            `}
          >
            Day {courseDay} Assignment
          </h2>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Question */}
          <div
            className={`
              px-6 py-4 border-b
              ${isDark ? "bg-[#1B1F27] border-[#232733]" : "bg-[#FAFAFA] border-[#E6E6EA]"}
            `}
          >
            <p
              className={`text-sm font-medium mb-2 ${
                isDark ? "text-[#9CA3B4]" : "text-[#5A5E69]"
              }`}
            >
              Question:
            </p>
            <p
              className={`
                text-base leading-relaxed font-['Poppins'] whitespace-pre-wrap
                ${isDark ? "text-[#E7EAF0]" : "text-[#2E2E2E]"}
              `}
            >
              {currentQuestion}
            </p>
          </div>

          {/* Input area */}
          <div className="px-6 py-6">
            {isDay13 ? (
              <div className="space-y-10">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-[#9CA3B4]" : "text-[#5A5E69]"
                    }`}
                  >
                    {currentQuestionData.label1 || "Response 1"}
                  </label>
                  <textarea
                    ref={textarea1Ref}
                    className={textareaClasses}
                    value={assignment1}
                    onChange={(e) => setAssignment1(e.target.value)}
                    placeholder="Type your first essay here..."
                  />
                  <p
                    className={`text-xs mt-2 text-right ${
                      isDark ? "text-[#5A5E69]" : "text-gray-500"
                    }`}
                  >
                    {assignment1.length} characters
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-[#9CA3B4]" : "text-[#5A5E69]"
                    }`}
                  >
                    {currentQuestionData.label2 || "Response 2"}
                  </label>
                  <textarea
                    ref={textarea2Ref}
                    className={textareaClasses}
                    value={assignment2}
                    onChange={(e) => setAssignment2(e.target.value)}
                    placeholder="Type your second essay here..."
                  />
                  <p
                    className={`text-xs mt-2 text-right ${
                      isDark ? "text-[#5A5E69]" : "text-gray-500"
                    }`}
                  >
                    {assignment2.length} characters
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-[#9CA3B4]" : "text-[#5A5E69]"
                  }`}
                >
                  Your Answer:
                </label>
                <textarea
                  ref={textarea1Ref}
                  className={textareaClasses}
                  value={assignment1}
                  onChange={(e) => setAssignment1(e.target.value)}
                  placeholder="Type your response here..."
                />
                <p
                  className={`text-xs mt-2 text-right ${
                    isDark ? "text-[#5A5E69]" : "text-gray-500"
                  }`}
                >
                  {assignment1.length} characters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`
            px-6 py-5 border-t flex justify-end space-x-4 flex-shrink-0
            ${isDark ? "bg-[#171A20] border-[#232733]" : "bg-[#F4F4F6] border-[#E6E6EA]"}
          `}
        >
          <button
            onClick={onClose}
            className={`
              px-5 py-2.5 rounded-lg font-medium transition-all
              ${
                isDark
                  ? "bg-[#1B1F27] text-[#E7EAF0] hover:bg-[#232733] border border-[#232733]"
                  : "bg-gray-100 text-[#2E2E2E] hover:bg-gray-200 border border-[#E6E6EA]"
              }
            `}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              (isDay13
                ? !assignment1.trim() || !assignment2.trim()
                : !assignment1.trim())
            }
            className={`
              px-6 py-2.5 rounded-lg font-medium transition-all min-w-[110px]
              ${
                isSubmitting ||
                (isDay13
                  ? !assignment1.trim() || !assignment2.trim()
                  : !assignment1.trim())
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-[#069c27] text-white hover:bg-[#05bd42] hover:shadow-md"
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting…
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>

      {isSubmitting &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] cursor-wait">
            <svg
              className="animate-spin h-12 w-12 text-[#069c27]"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default LongInputModal;
